import { useRouter } from 'next/router';
import { signIn, useSession } from 'next-auth/react';

import { Button } from '@/components/button';
import { AuthenticatedWrapper } from '@/components/modules/authenticated-wrapper';
import { Landing } from '@/components/modules/landing';
import { useFeatureNotifications } from '@/hooks/use-feature-notifications';
import { NEXT_AUTH_STATUSES, SIGN_IN_PAGE_URL, USER_ACCESS_FEATURES } from '@/lib/constants';
import { FEATURE } from '@/lib/constants/feature-notifications';
import { getUpgradeAccountUrl } from '@/utils/helpers/urls';
import { trackUser } from '@/utils/tracking';

interface TrackingAuthWrapperProps {
  children: React.ReactElement;
  entitlement?: (typeof USER_ACCESS_FEATURES)[keyof typeof USER_ACCESS_FEATURES];
}

const TrackingAuthWrapper = ({ children, entitlement = USER_ACCESS_FEATURES.TRACKING }: TrackingAuthWrapperProps) => {
  const router = useRouter();
  const { status } = useSession();
  const { isFeatureSeen, setFeatureSeen } = useFeatureNotifications(FEATURE.TRACKING_NAV);

  if (!isFeatureSeen) {
    setFeatureSeen();
  }

  const handleCreateAccount = async () => {
    router.push({
      pathname: SIGN_IN_PAGE_URL,
      query: { createAccount: true },
    });

    trackUser.event('Go to: Create account', {
      location: 'Tracking',
    });
  };

  const handleSignIn = async () => {
    await signIn();
    trackUser.event('Go to: Sign-in', {
      location: 'Tracking',
    });
  };

  const handleUpgrade = async () => {
    trackUser.event('Go to: Upgrade Account', {
      location: 'Tracking',
    });
  };

  return (
    <AuthenticatedWrapper
      entitlement={entitlement}
      fallback={
        <Landing
          actions={
            status === NEXT_AUTH_STATUSES.UNAUTHENTICATED ? (
              <>
                <Button onClick={handleSignIn}>Sign in to your account</Button>
                <Button onClick={handleCreateAccount} variant="primaryLightOutline">
                  Create an account
                </Button>
              </>
            ) : (
              <Button href={getUpgradeAccountUrl()} onClick={handleUpgrade}>
                Upgrade your account
              </Button>
            )
          }
          heading="Fluent Cargo Shipment Tracking"
          subHeading={
            <span className="block rounded-lg border border-green-100 bg-white px-3 py-4 text-center">
              For a limited time we are offering a free trial! Simply enter the coupon{' '}
              <b className="bg-green-100 p-0.5">TRACKMYSTUFF</b> when purchasing a subscription to get your first month
              free!
            </span>
          }
          imageUrl="https://www.datocms-assets.com/52833/1722390636-flc-tracking.png"
          imageAlt="Fluent tracking"
        />
      }
    >
      {children}
    </AuthenticatedWrapper>
  );
};

export { TrackingAuthWrapper };
